import React from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { SITE_URL, getSiteName, getDefaultOgImage } from "./routeConfig";
import { useContent } from "../context/ContentContext";

// Uniform, absolute URL for canonical/OG usage.
const absolute = (path = "/") => {
  if (!path) return SITE_URL;
  if (/^https?:/i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

// A single component every public page uses to declare its SEO. Admin pages
// pass `noindex` so nothing behind /admin/* ever ends up in a search engine.
// Reads defaults (site name, share image) live from Supabase via ContentContext
// so admin changes flow into every page without a code change.
export const SEO = ({
  title,
  description,
  path,          // path portion only; combined with SITE_URL for canonical
  ogImage,
  ogType = "website",
  noindex = false,
  jsonLd,        // optional array or object of structured data
  article,       // { publishedTime, modifiedTime, section, tags }
}) => {
  const { content } = useContent() || {};
  const canonical = absolute(path || (typeof window !== "undefined" ? window.location.pathname : "/"));
  const image = ogImage
    ? (/^https?:/i.test(ogImage) ? ogImage : absolute(ogImage))
    : getDefaultOgImage(content);
  const siteName = getSiteName(content);
  const jsonLdArr = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}

      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter (works for any card scraper — LinkedIn, Slack, iMessage etc.) */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* Article-specific meta */}
      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
      {article?.section && <meta property="article:section" content={article.section} />}
      {(article?.tags || []).map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}

      {jsonLdArr.map((data, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(data)}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
